import type { InventoryListItem } from '@/lib/api/schemas/inventory.schema'
import type { YmEconomicsPromo } from '@/lib/api/schemas/ymarket-pricing.schema'
import { formatRubleAmount, formatRublePerGram } from '@/lib/market-money'
import { unavailableReasonText } from '@/lib/warehouse/labels'
import { ATTRACTIVENESS_LABELS, syncText } from '@/lib/warehouse/ymarket-showcase'

type Economics = NonNullable<InventoryListItem['ymarketEconomics']>

const PROMO_STATUS: Record<string, string> = {
  AUTO: 'добавлено автоматически',
  PARTIALLY_AUTO: 'добавлено частично автоматически',
  MANUAL: 'добавлено вручную',
  NOT_PARTICIPATING: 'не участвует',
  RENEWED: 'продлено',
  RENEW_FAILED: 'не удалось продлить',
  MINIMUM_FOR_PROMOS: 'добавлено по «Минимуму для акций»',
  UNKNOWN: 'статус неизвестен',
}

interface Modifier {
  key: string
  label: string
  value: string
  source: string
  at: string | null
}

function moment(value: string | null | undefined): string | null {
  if (!value) return null
  return new Date(value).toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Moscow' })
}

function day(value: string | null | undefined): string | null {
  if (!value) return null
  return new Date(`${value}T00:00:00Z`).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', timeZone: 'UTC' })
}

function percent(value: number): string {
  return `${value.toLocaleString('ru-RU', { maximumFractionDigits: 2 })}%`
}

function promoValue(promo: YmEconomicsPromo): string {
  const status = promo.participating && promo.offerStatus === null ? 'участвует' : PROMO_STATUS[promo.offerStatus ?? 'UNKNOWN'] ?? promo.offerStatus ?? 'статус неизвестен'
  const price = promo.promoPrice === null ? '' : ` · цена в акции ${formatRubleAmount(promo.promoPrice)}`
  const until = promo.endsAt === null ? '' : ` · до ${moment(promo.endsAt)}`
  return `${status}${price}${until}`
}

/** Every Market layer between our price and the showcase, each with where it came from and when. */
export function marketModifiers(economics: Economics, weightGrams: number | null): Modifier[] {
  const prices = economics.prices
  const showcase = economics.showcase
  const collected = prices?.collectedAt ?? economics.snapshotCollectedAt
  const ours = prices?.ymPrice ?? economics.sellsFor
  const rows: Modifier[] = []
  if (ours !== null) rows.push({ key: 'price', label: 'Наша цена на Маркете', value: formatRubleAmount(ours), source: 'цены Маркета', at: moment(collected) })
  const struck = prices?.ymDiscountBase ?? null
  if (struck !== null) {
    const discount = ours !== null && struck > ours ? ` · наша скидка −${percent((1 - ours / struck) * 100)}` : ''
    rows.push({ key: 'struck', label: 'Зачёркнутая цена', value: `${formatRubleAmount(struck)}${discount}`, source: 'цены Маркета', at: moment(collected) })
  }
  const minimum = prices?.ymMinimumForPromos ?? null
  rows.push({ key: 'minimum', label: 'Минимум для акций', value: minimum === null ? 'не задан' : formatRubleAmount(minimum), source: 'цены Маркета', at: moment(collected) })
  for (const promo of economics.promos) {
    rows.push({ key: `promo-${promo.promoId}`, label: `Акция «${promo.name}»`, value: promoValue(promo), source: 'акции Маркета', at: moment(economics.snapshotCollectedAt) })
  }
  const report = showcase?.reportPrice ?? null
  if (report !== null && ours !== null && ours > 0) {
    rows.push({ key: 'market-discount', label: 'Скидка Маркета', value: `${formatRubleAmount(ours)} → ${formatRubleAmount(report)} (−${percent((1 - report / ours) * 100)})`, source: 'отчёт «Цены»', at: moment(showcase?.collectedAt) })
  }
  const noPayShown = showcase?.noPayPrice ?? null
  const formulaSource = showcase?.formulaSetAt ? 'формула витрины из настроек' : 'формула витрины по умолчанию'
  if (report !== null && noPayShown !== null && report > 0) {
    const factor = noPayShown / report
    rows.push({
      key: 'formula',
      label: 'Распродажа Маркета',
      value: `${formatRubleAmount(report)} → ${formatRubleAmount(noPayShown)} (×${factor.toLocaleString('ru-RU', { maximumFractionDigits: 4 })}, −${percent((1 - factor) * 100)})`,
      source: formulaSource,
      at: moment(showcase?.formulaSetAt),
    })
  }
  const payShown = showcase?.payPrice ?? null
  if (payShown !== null && noPayShown !== null && noPayShown > 0 && payShown !== noPayShown) {
    const reportPay = showcase?.reportPayPrice ?? null
    rows.push({
      key: 'pay',
      label: 'Яндекс Пэй',
      value: `−${percent((1 - payShown / noPayShown) * 100)}`,
      source: reportPay === null ? formulaSource : `отчёт «Цены» с Пэй ${formatRubleAmount(reportPay)}, ${formulaSource}`,
      at: moment(showcase?.formulaSetAt),
    })
  }
  const boost = economics.boost
  if (boost !== null) {
    const campaigns = boost.campaigns.map((name) => `«${name}»`).join(', ')
    rows.push({
      key: 'boost',
      label: 'Буст продаж',
      value: boost.bidPercent !== null
        ? `ставка ${percent(boost.bidPercent)}`
        : boost.campaigns.length > 0
          ? `в кампании ${campaigns}, ставка кампании в API не отдаётся`
          : unavailableReasonText(boost.unavailableReason ?? 'bids_not_set'),
      source: boost.bidPercent !== null ? 'ставки Маркета' : boost.campaigns.length > 0 ? `отчёт по бусту ${day(boost.reportOn) ?? ''}`.trim() : 'ставки Маркета',
      at: moment(collected),
    })
  }
  if (economics.attractiveness !== null) {
    const attractiveness = economics.attractiveness
    rows.push({ key: 'attractiveness', label: 'Привлекательность цены', value: `${ATTRACTIVENESS_LABELS[attractiveness.level]} (выгодно до ${formatRubleAmount(attractiveness.greenThreshold)}, завышена выше ${formatRubleAmount(attractiveness.redThreshold)})`, source: 'отчёт «Цены»', at: moment(attractiveness.collectedAt) })
  }
  const check = economics.syncCheck
  if (check !== null) {
    rows.push({ key: 'sync', label: check.drifted ? 'Сверка с заказом — расходится' : 'Сверка с заказом', value: syncText(check) ?? '', source: 'заказы Маркета', at: moment(check.orderedAt) })
  }
  const noPay = showcase?.noPayPrice ?? null
  if (noPay !== null) {
    const perGram = weightGrams !== null && weightGrams > 0 ? ` · ${formatRublePerGram(noPay / weightGrams)}` : ''
    const pay = showcase?.payPrice !== null && showcase?.payPrice !== undefined && showcase.payPrice !== noPay ? ` · с Пэй ${formatRubleAmount(showcase.payPrice)}` : ''
    rows.push({ key: 'showcase', label: 'Итог на витрине', value: `${formatRubleAmount(noPay)}${perGram}${pay}`, source: 'расчёт Forge', at: moment(showcase?.collectedAt) })
  }
  return rows
}

export function MarketModifiers({ economics, weightGrams }: { economics: Economics; weightGrams: number | null }) {
  const rows = marketModifiers(economics, weightGrams)
  return (
    <details className="mt-2 text-xs text-ink-2" data-testid="market-modifiers">
      <summary className="cursor-pointer text-ink-muted">Модификаторы Маркета</summary>
      <dl className="mt-2 space-y-1.5">
        {rows.map((row) => (
          <div key={row.key} data-modifier={row.key}>
            <dt className="font-medium text-ink">{row.label}</dt>
            <dd className="tabular-nums">
              {row.value}
              <span className="block text-[11px] text-ink-muted">{row.source}{row.at === null ? '' : ` · ${row.at}`}</span>
            </dd>
          </div>
        ))}
      </dl>
    </details>
  )
}
